import React from "react";
import { Edit3 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { User } from "../types/types";

interface InfoCard {
  icon?: LucideIcon;
  label: string;
  value: string | React.ReactNode;
  customIcon?: React.ReactNode;
}

interface ProfileLayoutProps {
  user: User;
  infoCards: InfoCard[];
  isEditable?: boolean;
  onEdit?: () => void;
}

const ProfileLayout: React.FC<ProfileLayoutProps> = ({
  user,
  infoCards,
  isEditable = false,
  onEdit,
}) => {
  return (
    <main className="w-full max-w-5xl mx-auto px-6 md:px-12 py-8 md:py-12">
      {/* Profile Hero - LinkedIn Inspired */}
      <section className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden mb-8">
        {/* Cover Photo Placeholder */}
        <div className="h-40 md:h-56 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 relative">
          <div className="absolute inset-0 bg-black/10"></div>

          {/* Avatar - Centered on the boundary */}
          <div className="absolute left-1/2 top-full -translate-x-1/2 -translate-y-1/2">
            <div className="relative group">
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-white shadow-xl ring-2 ring-orange-100 group-hover:ring-orange-300 transition-all duration-300">
                <img
                  src={user.photo_url}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="relative px-6 md:px-10 pb-8 pt-20 md:pt-24">
          {/* Content */}
          <div className="md:pl-52">
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-1">
              {user.name}
            </h1>
            <p className="text-lg text-zinc-700 mb-3 max-w-2xl">{user.bio}</p>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-3 text-sm text-zinc-600">
              <span className="flex items-center gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-4 h-4 text-orange-600"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 8.25h.75m0 0H18m-13.5-0h13.5M9 6.75h.75m5.25 0h.75M9 15h.75m5.25 0h.75M21 4.5v1.5m0 0v1.5m0-1.5h-1.5m1.5 0h1.5"
                  />
                </svg>
                {user.company}
              </span>
            </div>

            {/* Role Badge with Edit */}
            <div className="mt-4 flex items-center gap-2">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold rounded-full shadow-md">
                <span className="w-1.5 h-1.5 bg-white/80 rounded-full animate-pulse"></span>
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </div>
              {isEditable && onEdit && (
                <button
                  onClick={onEdit}
                  className="p-1.5 bg-white/80 hover:bg-white rounded-full shadow-sm transition-colors text-orange-600 hover:text-orange-700"
                  title="Edit Profile"
                >
                  <Edit3 size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Info Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {infoCards.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-xl border border-zinc-200 p-6 hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
                {item.customIcon ||
                  (item.icon && (
                    <item.icon className="w-6 h-6 text-orange-600" />
                  ))}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-orange-600 mb-1">
                  {item.label}
                </p>
                <p className="text-base font-medium text-zinc-900 break-all">
                  {item.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
};

export default ProfileLayout;
